import { SOCIAL_PROFILES, SOCIAL_URLS } from './socials';

/**
 * Author registry for blog articles and author pages.
 *
 * Today there is a single author (Dimitris). The registry is keyed by slug so
 * additional authors can be added without touching the article/SEO code.
 */

export interface AuthorProfile {
  slug: string;
  /** Stable @id used across BlogPosting / Person / ProfilePage schema. */
  id: string;
  name: string;
  jobTitleEn: string;
  jobTitleEl: string;
  bioEn: string;
  bioEl: string;
  shortBioEn: string;
  shortBioEl: string;
  /** Absolute or app-relative image URL. */
  image: string;
  /** External profiles for schema `sameAs`. */
  sameAs: string[];
  knowsAbout: string[];
  credentialsEn: string[];
  credentialsEl: string[];
}

export const SITE_URL = 'https://eyecaptain.io';

export const AUTHORS: Record<string, AuthorProfile> = {
  dimitris: {
    slug: 'dimitris',
    id: `${SITE_URL}/#author-dimitris`,
    name: 'Dimitris Andreadakis',
    jobTitleEn: 'Founder & CRO Expert',
    jobTitleEl: 'Founder & CRO Expert',
    // The avatar the author page shows. There is no portrait in public/ yet;
    // /authors/dimitris.jpg never existed. Keep in step with seo-ssr.ts.
    image: `${SITE_URL}/icon-512.png`,
    sameAs: [SOCIAL_PROFILES.linkedin],
    knowsAbout: [
      'Conversion Rate Optimization',
      'UX Analysis',
      'Neuromarketing',
      'Heatmap Analysis',
      'A/B Testing',
    ],
    credentialsEn: [
      'Founder of EyeCaptain, an AI-powered CRO and UX analysis platform',
      '10+ years running conversion programs for e-commerce and lead generation sites',
      'Specialises in neuromarketing-driven page architecture and attention analysis',
      'Built the EyeCaptain CRO rubric: 48 audited criteria across clarity, trust, friction and persuasion',
    ],
    credentialsEl: [
      'Founder του EyeCaptain, πλατφόρμας CRO και UX ανάλυσης με AI',
      '10+ χρόνια σε conversion programs για e-commerce και lead generation sites',
      'Εξειδίκευση σε neuromarketing page architecture και attention analysis',
      'Δημιουργός του EyeCaptain CRO rubric: 48 κριτήρια σε clarity, trust, friction και persuasion',
    ],
    shortBioEn:
      'Founder of EyeCaptain. Writes about conversion rate optimization, UX analysis and the neuromarketing behind pages that actually sell.',
    shortBioEl:
      'Founder του EyeCaptain. Γράφει για conversion rate optimization, UX ανάλυση και το neuromarketing πίσω από σελίδες που πραγματικά πουλάνε.',
    bioEn:
      'Dimitris Andreadakis is the founder of EyeCaptain, an AI-powered CRO and UX analysis platform used by e-commerce teams, agencies and lead generation businesses. Over the last decade he has run conversion programs across hundreds of pages, from checkout flows and product detail pages to B2B landing pages and pricing tables. His work sits at the intersection of behavioural psychology and hard analytics: what visitors actually look at, where attention breaks down, and which page elements carry the weight of a decision. At EyeCaptain he designed the scoring rubric behind every audit, a 48-criteria framework covering clarity, trust, friction, persuasion and visual hierarchy, and the attention model that ranks the elements competing for a visitor on any given screen. He writes here about conversion rate optimization, UX auditing, heatmap interpretation, A/B testing discipline and the neuromarketing principles that separate a page that gets traffic from a page that converts it.',
    bioEl:
      'Ο Δημήτρης Ανδρεαδάκης είναι founder του EyeCaptain, μιας πλατφόρμας CRO και UX ανάλυσης με AI που χρησιμοποιείται από e-commerce ομάδες, agencies και lead generation επιχειρήσεις. Την τελευταία δεκαετία έχει τρέξει conversion programs σε εκατοντάδες σελίδες, από checkout flows και product pages μέχρι B2B landing pages και pricing tables. Η δουλειά του συνδυάζει behavioural psychology και σκληρά analytics: τι κοιτάει πραγματικά ο επισκέπτης, πού σπάει η προσοχή και ποια στοιχεία σηκώνουν το βάρος της απόφασης. Στο EyeCaptain σχεδίασε το scoring rubric κάθε audit, ένα framework 48 κριτηρίων σε clarity, trust, friction, persuasion και visual hierarchy, καθώς και το attention model που κατατάσσει τα στοιχεία που ανταγωνίζονται για την προσοχή σε κάθε οθόνη. Γράφει εδώ για conversion rate optimization, UX auditing, ερμηνεία heatmaps, A/B testing και τις αρχές neuromarketing που ξεχωρίζουν μια σελίδα που παίρνει επισκεψιμότητα από μια σελίδα που τη μετατρέπει.',
  },
};

export const DEFAULT_AUTHOR_SLUG = 'dimitris';

export const getAuthor = (slug?: string | null): AuthorProfile =>
  (slug && AUTHORS[slug]) || AUTHORS[DEFAULT_AUTHOR_SLUG];

export const getAuthorUrl = (slug: string) => `${SITE_URL}/authors/${slug}`;

/** Person node reused by article schema and the author page. */
export const buildPersonSchema = (author: AuthorProfile, isEl = false) => ({
  "@type": "Person",
  "@id": author.id,
  name: author.name,
  jobTitle: isEl ? author.jobTitleEl : author.jobTitleEn,
  description: isEl ? author.shortBioEl : author.shortBioEn,
  url: getAuthorUrl(author.slug),
  image: author.image,
  sameAs: author.sameAs,
  knowsAbout: author.knowsAbout,
  worksFor: { "@id": `${SITE_URL}/#organization` },
});

export const buildOrganizationNode = () => ({
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "EyeCaptain",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo.png`,
  },
  sameAs: SOCIAL_URLS,
});
